import {
  APPOINTMENT_STATUS,
  Appointment,
  MEDICAL_HISTORY_CATEGORY,
  MedicalHistory,
  PatientProfile,
  TFilterExpressionElement,
  TFilterExpression,
  TWorkflowField,
  VIEW_FILTERS,
  VIEW_APT_STATUS_MAP,
  TFilterOption,
} from "@elixir/types";
import { getAge } from "./date-utils";
import { Filter } from "mongodb";
import dateFormat from "dateformat";

export const getCount = (data?: Appointment[]) => {
  return (data?.length || 0).toString().padStart(2, "0");
};

export const aptNoShowStatuses = [
  APPOINTMENT_STATUS.NO_SHOW,
  APPOINTMENT_STATUS.NO_SHOW_DOCTOR,
  APPOINTMENT_STATUS.NO_SHOW_STAFF,
];

export const aptCancelledStatuses = [
  APPOINTMENT_STATUS.CANCELLED,
  APPOINTMENT_STATUS.DOCTOR_CANCELLED,
  APPOINTMENT_STATUS.STAFF_CANCELLED,
];

export const rescheduledStatuses = [
  APPOINTMENT_STATUS.RESCHEDULED,
  APPOINTMENT_STATUS.DOCTOR_RESCHEDULED,
  APPOINTMENT_STATUS.STAFF_RESCHEDULED,
];

export const getPatientMainInfoString = ({
  patient,
}: {
  patient: Pick<PatientProfile, "fln" | "gen" | "dob">;
}) => {
  const patientInfoArray: Array<string> = [];

  const patientName = patient.fln;
  patientInfoArray.push(patientName);

  const patientGender = patient?.gen;
  if (patientGender) {
    patientInfoArray.push(patientGender);
  }

  const patientAge = patient.dob ? getAge(new Date(patient.dob)) : "";
  if (patientAge) {
    patientInfoArray.push(patientAge);
  }

  return patientInfoArray;
};

export const printRx = async (pxUrl: string | undefined) => {
  const iframeAlreadyPresent = document.getElementById("print_receipt");
  if (iframeAlreadyPresent) {
    document.body.removeChild(iframeAlreadyPresent);
  }

  if (!pxUrl) return;

  const blob = await fetch(pxUrl).then((r) => r.blob());
  const url = URL.createObjectURL(blob);

  const i = document.createElement("iframe");
  i.style.display = "none";
  i.id = "print_receipt";
  i.src = url;

  document.body.appendChild(i);

  i.addEventListener("load", function () {
    i.contentWindow?.focus();
    i.contentWindow?.print();
    return;
  });
};

export const addUhidToAppointment = (
  apts: (Appointment & { uhid?: string })[]
): Array<Appointment & { uhid?: string }> => {
  if (!apts || apts.length === 0) return [];
  for (let i = 0; i < apts.length; i++) {
    const uhid = apts[i].formData?.find((h) => h.key === "uhid")?.value;
    apts[i] = { ...apts[i], uhid: uhid };
  }
  return apts;
};

export type TColorChip = {
  textColor: string;
  bgColor: string;
};

export const colorsToBeChosen: Array<TColorChip> = [
  {
    textColor: "#D75C86",
    bgColor: "#F2DEE5",
  },
  {
    textColor: "#EC6657",
    bgColor: "#FFEAE3",
  },
  {
    textColor: "#A624A0",
    bgColor: "#FFECFE",
  },
  {
    textColor: "#CE9A12",
    bgColor: "#FFF5DB",
  },
  {
    textColor: "#30AC41",
    bgColor: "#E3F8E6",
  },
  {
    textColor: "#149CFE",
    bgColor: "#E6F4FE",
  },
  {
    textColor: "#515BA7",
    bgColor: "#E5E7F3",
  },
];

// export const colorsToBeChosen: Array<TColorChip> = [
//   {
//     textColor: "#767676",
//     bgColor: "#F4F4F4",
//   },
//   {
//     textColor: "#1A1A1A",
//     bgColor: "#BABABA",
//   },
//   {
//     textColor: "#215FFF",
//     bgColor: "#E9EFFF",
//   },
//   {
//     textColor: "#143999",
//     bgColor: "#D3DFFF",
//   },
//   {
//     textColor: "#19A66A",
//     bgColor: "#E8F6F0",
//   },
//   {
//     textColor: "#0F6440",
//     bgColor: "#D1EDE1",
//   },
//   {
//     textColor: "#D74B41",
//     bgColor: "#FBEDEC",
//   },
//   {
//     textColor: "#812D27",
//     bgColor: "#F7DBD9",
//   },
//   {
//     textColor: "#EDAE0C",
//     bgColor: "#FDF7E7",
//   },
//   {
//     textColor: "#8E6807",
//     bgColor: "#FBEFCE",
//   },
// ];

export const getMedicalHistory = ({
  data,
}: {
  data: MedicalHistory;
}): {
  name: string;
  active: boolean;
}[] => {
  const list = new Set<{ name: string; active: boolean }>();
  (Object.keys(data) as MEDICAL_HISTORY_CATEGORY[]).forEach((key) => {
    data?.[key]?.map((item: any) => {
      list.add({ name: item.name, active: item.status === "Active" });
    });
  });
  return Array.from(list);
};

export const getFilterExpressionBasedOnAptData = (apt: Appointment) => {
  const filterExpression: {
    id: VIEW_FILTERS;
    options:
      | {
          value: string | number;
        }[]
      | undefined;
  }[] = [];

  filterExpression.push({
    id: VIEW_FILTERS.TAGS,
    options: apt.tags?.map((tag) => {
      return {
        value: tag,
      };
    }),
  });

  filterExpression.push({
    id: VIEW_FILTERS.APPOINTMENT_CHANNEL,
    options: [{ value: apt.channel as string }],
  });

  filterExpression.push({
    id: VIEW_FILTERS.LABELS,
    options: [{ value: apt.label as string }],
  });

  filterExpression.push({
    id: VIEW_FILTERS.APPOINTMENT_MODE,
    options: [{ value: apt.mode as string }],
  });

  filterExpression.push({
    id: VIEW_FILTERS.APPOINTMENT_STATUS,
    options: [
      { value: VIEW_APT_STATUS_MAP[apt.status] ?? (apt.status as string) },
    ],
  });

  filterExpression.push({
    id: VIEW_FILTERS.SERVICES,
    options: apt.receipts?.flatMap((receipt) => {
      return receipt.receipt_sku?.map(({ sku_item }) => {
        const { service_name, price } = sku_item;
        const lowerCaseServiceName = service_name.toLowerCase();
        return {
          value: `${lowerCaseServiceName}_${price}`,
        };
      });
    }),
  });

  filterExpression.push({
    id: VIEW_FILTERS.DOCTOR,
    options: [{ value: apt.doctor_oid }],
  });

  filterExpression.push({
    id: VIEW_FILTERS.PAYMENT_STATUS,
    options: [{ value: apt.billStatus }],
  });

  filterExpression.push({
    id: VIEW_FILTERS.SELF_ASSESSMENTS,
    options: [
      {
        value: apt.sa_cmpl?.length
          ? "true"
          : Object.keys(apt.assessments || {}).length > 0
            ? "true"
            : "false",
      },
    ],
  });

  if (apt.apt_fields) {
    apt.apt_fields.forEach((field) => {
      filterExpression.push({
        id: field.field_id as VIEW_FILTERS,
        options: field.values.map((value) => ({
          value: value,
        })),
      });
    });
  }

  return filterExpression;
};

export const getFilterExpressionFromOptions = (filterOptions: {
  [key: string]: TWorkflowField;
}): Filter<TFilterOption> => {
  const initialFilterExpression: TFilterExpression = {
    $and: [],
  };
  const filterExpression = Object.keys(filterOptions).reduce<
    Filter<TFilterOption>
  >((acc, curr) => {
    const { options, title } = filterOptions[curr];
    if (!options || options.length === 0) return acc;
    const atomFilterExpression: TFilterExpressionElement = {
      id: title,
      options: {
        $elemMatch: {
          $or: options.map((option) => ({
            value: option.value,
          })),
        },
      },
    };
    return {
      ...acc,
      $and: [...(acc.$and || []), atomFilterExpression],
    };
  }, initialFilterExpression);
  return filterExpression;
};

/*
Clinic Level Apts utilities
*/

export function isClinicLevelApt({ docId }: { docId: string }) {
  try {
    return docId?.startsWith("c-");
  } catch (err) {
    console.error("isClinicLevelApt_broke", err);
  }
}

export function getDocIdFromClinicIdForApt({ clinicId }: { clinicId: string }) {
  return `c-${clinicId}`;
}

export const defDocOfBusiness: Record<string, string> = {
  "75682005283056": "172242251423737",
  "10000": "161419272566611",
};

// export function isEligbileForClinicApts(businessId: string) {
//   const queueConfig = localStorage.getItem(QUEUE_CONFIG_KEY);
//   if (queueConfig) {
//     const parsedQueueConfig = JSON.parse(queueConfig);
//     if (parsedQueueConfig?.apt_config?.allow_clinic_booking) {
//       return true;
//     }
//   }
//   // return !!defDocOfBusiness?.[businessId];
// }

/*
Clinic Level Apts utilities end
*/

export function getAppointmentFetchConfigKey({
  clinicId,
  date,
}: {
  clinicId: string;
  date: string | number | Date;
}) {
  return `aptFetchConfig_${clinicId}_${dateFormat(
    new Date(date),
    "yyyy-mm-dd"
  )}`;
}
